/**
 * Bounded exponential backoff with jitter for SSE reconnect timers.
 *
 * Both `useChatStream` and `useNotificationStream` previously
 * scheduled `setTimeout(connect, 5_000)` on every `EventSource`
 * error. Under steady-state network blips that's fine, but when
 * an upstream blip (e.g. a brief 5xx wave from the edge or a
 * Railway restart) coincides with a fan of open SSE tabs, a flat
 * 5s timer hammers the same error every cycle and the stream
 * never recovers gracefully.
 *
 * The backoff schedule below caps out at ~30s (well above any
 * realistic transient and low enough that the user doesn't
 * notice the gap on real network recovery) and adds up to 1s of
 * jitter to desynchronise reconnects across tabs.
 *
 * Lives in its own module so both SSE hooks share the schedule
 * and so the math is pure-testable without spinning up a real
 * `EventSource`.
 */

const BASE_DELAY_MS = 5_000;
const MAX_DELAY_MS = 30_000;
const JITTER_MAX_MS = 1_000;

/**
 * Compute the delay (ms) before the Nth reconnect attempt.
 *
 * `attempt` is zero-indexed: 0 = first reconnect after a fresh
 * failure, 1 = second consecutive failure, etc. Reset to 0 on
 * any successful `connected` event.
 *
 * Schedule (without jitter):
 *   attempt 0 → 5s
 *   attempt 1 → 10s
 *   attempt 2 → 20s
 *   attempt 3+ → 30s (capped)
 *
 * `randomFn` is injectable so tests can pin the jitter without
 * stubbing `Math.random` globally.
 */
export function nextReconnectDelay(
  attempt: number,
  randomFn: () => number = Math.random,
): number {
  const safeAttempt = Math.max(0, Math.floor(attempt));
  const exponential = BASE_DELAY_MS * 2 ** safeAttempt;
  const capped = Math.min(exponential, MAX_DELAY_MS);
  const jitter = Math.floor(randomFn() * JITTER_MAX_MS);
  return capped + jitter;
}
